import { TimberItem, ParsedTimberList } from '../types/bidding';

/**
 * Parse timber list from CSV text
 * Handles various formats including tab-separated and with metadata rows
 * Expected format (with or without headers):
 *   Name, Material, Qty, Width, Height, Real Length, Order Length
 *   finial, Doug Fir, 4, 8", 8", 11 3/8", 2'
 * 
 * @param csvText - Raw CSV text
 * @returns Parsed timber items
 */
export function parseTimberListFromCSV(csvText: string): TimberItem[] {
  // Normalize line endings
  const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedText.split('\n').map(line => line.trim()).filter(line => line);
  
  if (lines.length < 1) {
    throw new Error('File is empty');
  }
  
  console.log('[CSV Parser] Processing', lines.length, 'lines');
  console.log('[CSV Parser] First line:', lines[0]);
  
  // Detect delimiter: tabs are common in exported timber lists
  const delimiter = detectDelimiter(lines[0]);
  console.log('[CSV Parser] Using delimiter:', delimiter === '\t' ? 'TAB' : 'COMMA');
  
  // Find where the actual data starts (skip metadata rows)
  let dataStartIndex = findDataStartIndex(lines, delimiter);
  console.log('[CSV Parser] Data starts at row:', dataStartIndex);
  
  // Parse items
  const items: TimberItem[] = [];
  
  for (let i = dataStartIndex; i < lines.length; i++) {
    const cells = splitLine(lines[i], delimiter);
    console.log(`[CSV Parser] Row ${i}:`, cells);
    
    // Need at least 6 columns for valid timber data
    if (cells.length < 6) {
      console.log(`[CSV Parser] Skipping row ${i}: too few columns (${cells.length})`);
      continue;
    }
    
    // Parse using fixed positions based on your format:
    // [0] Name, [1] Material, [2] Qty, [3] Width, [4] Height, [5] Real Length, [6] Order Length
    const name = cells[0]?.trim() || '';
    const material = cells[1]?.trim() || 'Doug Fir';
    const qtyStr = cells[2]?.trim() || '1';
    const widthStr = cells[3]?.trim() || '0';
    const heightStr = cells[4]?.trim() || '0';
    const realLengthStr = cells[5]?.trim() || '';
    const orderLengthStr = cells[6]?.trim() || cells[5]?.trim() || '';
    
    // Skip metadata rows
    if (isMetadataRow(name)) {
      console.log(`[CSV Parser] Skipping metadata row: ${name}`);
      continue;
    }
    
    // Parse quantity
    const quantity = parseInt(qtyStr.replace(/[^0-9]/g, '')) || 1;
    
    // Parse dimensions - extract just the number from strings like "8"" or "10""
    const width = parseDimension(widthStr);
    const height = parseDimension(heightStr);
    
    // Skip rows with invalid dimensions
    if (width === 0 || height === 0) {
      console.log(`[CSV Parser] Skipping row ${i}: invalid dimensions (${widthStr}, ${heightStr})`);
      continue;
    }
    
    const item: TimberItem = {
      id: generateId(),
      name: name.toLowerCase() || 'unnamed',
      material: material || 'Doug Fir',
      quantity: quantity,
      width: width,
      height: height,
      realLength: realLengthStr,
      orderLength: orderLengthStr,
      boardFeet: 0,
      unitPrice: 0,
      totalPrice: 0,
      manuallyEdited: false,
    };
    
    items.push(item);
    console.log('[CSV Parser] Parsed item:', item.name, `${item.quantity}x`, `${item.width}"x${item.height}"`, `@ ${item.orderLength}`);
  }
  
  console.log(`[CSV Parser] Successfully parsed ${items.length} items`);
  
  if (items.length === 0) {
    throw new Error('No valid timber items found. Expected format: Name, Material, Qty, Width, Height, Real Length, Order Length');
  }
  
  return items;
}

/**
 * Detect whether the file uses tabs or commas as delimiter
 */
function detectDelimiter(line: string): string {
  const tabCount = (line.match(/\t/g) || []).length;
  const commaCount = (line.match(/,/g) || []).length;
  
  // If more tabs than commas, use tab
  if (tabCount > commaCount) {
    return '\t';
  }
  return ',';
}

/**
 * Find where actual data starts (skip metadata like "3D file name", "Date", "Designer")
 */
function findDataStartIndex(lines: string[], delimiter: string): number {
  for (let i = 0; i < Math.min(10, lines.length); i++) {
    const cells = splitLine(lines[i], delimiter);
    
    // Check if this looks like a data row:
    // - Has at least 6 columns
    // - Has numeric quantity in column 2 or 3
    // - Has dimension-like values (numbers with " or ')
    if (cells.length >= 6) {
      const hasQty = cells.some((cell, idx) => idx >= 2 && /^\d+$/.test(cell.trim()));
      const hasDimension = cells.some(cell => /\d+["']/.test(cell));
      
      if (hasQty || hasDimension) {
        return i;
      }
    }
    
    // Skip obvious metadata rows
    const firstCell = cells[0]?.toLowerCase() || '';
    if (firstCell.includes('file') || firstCell.includes('date') || firstCell.includes('designer')) {
      continue;
    }
  }
  
  return 0; // Default to first row
}

/**
 * Check if a row is metadata (not actual timber data)
 */
function isMetadataRow(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.includes('file') || 
         lower.includes('date') || 
         lower.includes('designer') ||
         lower.includes('prod') ||
         lower.includes('name') && lower.includes('material');
}

/**
 * Split a line by delimiter, handling the inch/foot marks properly
 * This is a simplified approach that works for timber list CSVs
 */
function splitLine(line: string, delimiter: string): string[] {
  if (delimiter === '\t') {
    // Tab-delimited is straightforward
    return line.split('\t');
  }
  
  // For comma-delimited with dimension marks, use a regex approach
  // The key insight: actual CSV field separators have commas NOT preceded by a digit+quote
  // e.g., "finial,Doug Fir,4,8",8",11 3/8",2'"
  
  // Strategy: Replace inch/foot marks temporarily, split, then restore
  const INCH_PLACEHOLDER = '___INCH___';
  const FOOT_PLACEHOLDER = '___FOOT___';
  
  // Replace dimension marks that are part of values
  // Pattern: digit followed by " or '
  let processed = line.replace(/(\d)"/g, `$1${INCH_PLACEHOLDER}`);
  processed = processed.replace(/(\d)'/g, `$1${FOOT_PLACEHOLDER}`);
  
  // Now split by comma
  const cells = processed.split(',');
  
  // Restore the dimension marks
  return cells.map(cell => 
    cell
      .replace(new RegExp(INCH_PLACEHOLDER, 'g'), '"')
      .replace(new RegExp(FOOT_PLACEHOLDER, 'g'), "'")
      .trim()
  );
}

/**
 * Parse a dimension string like "8"" or "10"" to just the number
 */
function parseDimension(dimStr: string): number {
  if (!dimStr) return 0;
  
  // Extract just the leading number
  const match = dimStr.match(/^(\d+)/);
  if (match) {
    return parseInt(match[1]);
  }
  
  // Try to find any number in the string
  const numMatch = dimStr.match(/(\d+)/);
  return numMatch ? parseInt(numMatch[1]) : 0;
}


/**
 * Parse timber list from OCR-extracted text
 * This handles more varied/messy input from OCR
 * @param text - Extracted text from OCR
 * @returns Parsed timber items
 */
export function parseTimberListFromText(text: string): TimberItem[] {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  const items: TimberItem[] = [];
  
  // Common timber item names to look for
  const itemNames = [
    'post', 'corbel', 'rafter', 'arch', 'brace', 'beam', 'joist',
    'purlin', 'tie', 'plate', 'sill', 'collar', 'strut', 'fascia',
    'dragon', 'hip', 'placeholder', 'finial', 'short post'
  ];
  
  for (const line of lines) {
    // Skip header lines
    if (line.toLowerCase().includes('prod') && line.toLowerCase().includes('name')) {
      continue;
    }
    if (line.toLowerCase().includes('3d file') || line.toLowerCase().includes('designer')) {
      continue;
    }
    
    // Try to identify if this line contains a timber item
    const lineLower = line.toLowerCase();
    const foundName = itemNames.find(name => lineLower.includes(name));
    
    if (!foundName) continue;
    
    // Try to extract structured data
    // Pattern: name material qty widthxheight length
    const qtyMatch = line.match(/(\d+)\s+(\d+)["']?/);  // e.g., "6 8\"" or "4 4"
    const dimensionMatch = line.match(/(\d+)\s*[x×]\s*(\d+)/); // e.g., "6×8" or "6 x 8"
    const lengthMatch = line.match(/(\d+)[''](-\d+)?/); // e.g., "8'" or "12'-6"
    
    const quantity = qtyMatch ? parseInt(qtyMatch[1]) : 1;
    const width = dimensionMatch ? parseInt(dimensionMatch[1]) : (qtyMatch ? parseInt(qtyMatch[2]) : 0);
    const height = dimensionMatch ? parseInt(dimensionMatch[2]) : 0;
    const orderLength = lengthMatch ? lengthMatch[0] : '';
    
    // Extract material if present (look for patterns like "#1 GRN DF" or "Doug Fir")
    const materialMatch = line.match(/#\d+\s+[A-Z\s]+DF|Doug(?:las)?\s+Fir|Cedar|Pine|Spruce/i);
    const material = materialMatch ? materialMatch[0] : '';
    
    items.push({
      id: generateId(),
      name: foundName,
      material: material,
      quantity: quantity,
      width: width,
      height: height,
      realLength: '',
      orderLength: orderLength,
      boardFeet: 0,
      unitPrice: 0,
      totalPrice: 0,
      confidence: 0.7, // OCR has inherent uncertainty
      manuallyEdited: false,
    });
  }
  
  return items;
}

/**
 * Parse ORDER LENGTH string to inches
 * Handles formats like: "8'", "10'", "12'-6 3/8\"", "14'-9 11/16\""
 * @param lengthStr - Length string from CSV
 * @returns Length in inches
 */
export function parseOrderLength(lengthStr: string): number {
  if (!lengthStr) return 0;
  
  // Remove extra spaces
  const cleaned = lengthStr.trim();
  
  // Match feet and optional inches with fractions
  // Examples: "8'", "12'-6", "14'-9 11/16\"
  const pattern = /(\d+)[''](?:-(\d+)\s*(\d+\/\d+)?)?["]?/;
  const match = cleaned.match(pattern);
  
  if (!match) {
    // Try simpler format: just a number (assume feet)
    const simpleMatch = cleaned.match(/^(\d+)$/);
    if (simpleMatch) {
      return parseInt(simpleMatch[1]) * 12;
    }
    return 0;
  }
  
  const feet = parseInt(match[1]) || 0;
  const inches = parseInt(match[2]) || 0;
  const fraction = match[3] ? parseFraction(match[3]) : 0;
  
  return (feet * 12) + inches + fraction;
}

/**
 * Parse a fraction string like "3/4" or "11/16" to decimal
 */
function parseFraction(fractionStr: string): number {
  const parts = fractionStr.split('/');
  if (parts.length !== 2) return 0;
  
  const numerator = parseInt(parts[0]);
  const denominator = parseInt(parts[1]);
  
  if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
    return 0;
  }
  
  return numerator / denominator;
}

/**
 * Calculate board feet for a timber item
 * Formula: (width × height × length in inches) ÷ 144
 */
export function calculateBoardFeet(item: TimberItem): number {
  const lengthInInches = parseOrderLength(item.orderLength);
  
  if (item.width === 0 || item.height === 0 || lengthInInches === 0) {
    return 0;
  }
  
  return (item.width * item.height * lengthInInches) / 144;
}

/**
 * Update a timber item with calculated board feet
 */
export function updateTimberItemBoardFeet(item: TimberItem): TimberItem {
  return {
    ...item,
    boardFeet: calculateBoardFeet(item),
  };
}

/**
 * Generate a unique ID for timber items
 */
function generateId(): string {
  return `timber-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validate a timber item has required fields
 */
export function validateTimberItem(item: TimberItem): string[] {
  const errors: string[] = [];
  
  if (!item.name) {
    errors.push('Name is required');
  }
  if (item.quantity <= 0) {
    errors.push('Quantity must be greater than 0');
  }
  if (item.width <= 0) {
    errors.push('Width must be greater than 0');
  }
  if (item.height <= 0) {
    errors.push('Height must be greater than 0');
  }
  if (!item.orderLength) {
    errors.push('Order length is required');
  }
  
  return errors;
}

/**
 * Parse timber list with full metadata extraction
 */
export function parseTimberListWithMetadata(csvText: string): ParsedTimberList {
  const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
  const metadata: ParsedTimberList['metadata'] = {};
  const parseErrors: string[] = [];
  
  // Look for metadata in first few lines
  for (let i = 0; i < Math.min(5, lines.length); i++) {
    const line = lines[i];
    
    if (line.includes('3D file name')) {
      const match = line.match(/3D file name[:\s]*(.+)/i);
      metadata.fileName = match ? match[1].trim() : undefined;
    }
    if (line.includes('Date')) {
      const match = line.match(/Date[:\s]*(.+)/i);
      metadata.dateDesigned = match ? match[1].trim() : undefined;
    }
    if (line.includes('Designer')) {
      const match = line.match(/Designer[:\s]*(.+)/i);
      metadata.designer = match ? match[1].trim() : undefined;
    }
  }
  
  // Parse items
  let items: TimberItem[] = [];
  try {
    items = parseTimberListFromCSV(csvText);
  } catch (error) {
    parseErrors.push(error instanceof Error ? error.message : 'Failed to parse CSV');
  }
  
  return {
    items,
    metadata,
    parseErrors: parseErrors.length > 0 ? parseErrors : undefined,
  };
}
